/**
 * Shared loader helpers for react-router routes.
 *
 * Loaders run on every navigation and on the initial render. They are
 * the SPA equivalent of the old Next.js Server Component bodies — but
 * they execute in the browser, fetch through `lib/api`, and rely on
 * the gateway's ``kos_session`` cookie for auth.
 *
 * On 401 the helpers throw a `redirect("/login?from=...")` so React
 * Router unmounts the dashboard subtree and navigates the user to the
 * sign-in screen with their original destination preserved.
 *
 * ### Refresh cache
 *
 * To kill the white-screen-on-refresh, ``/v1/me`` and ``/v1/me/teams``
 * are mirrored into sessionStorage. On the next refresh the loader
 * returns the cached value synchronously and kicks off a background
 * revalidation; mismatches are written back so the next navigation
 * picks up the fresh data. Team switch / create uses
 * ``primeActiveTeamCaches`` to write the new workspace immediately
 * before revalidation; profile edit / sign-out still call
 * ``invalidateAuthCaches`` because those need a fresh server value or
 * no value at all.
 */

import { redirect } from "react-router-dom";

import { ApiError, api } from "@/lib/api";
import type { MeResponse } from "@/lib/types";

import { type AuthState, meToAuthState } from "./auth";

const ME_CACHE_KEY = "kos.cache.me";
const TEAMS_CACHE_KEY = "kos.cache.teams";

/** Workspace-switcher payload, mirrored from `_dashboard-shell.tsx`. */
export interface CachedTeam {
  team_id: string;
  team_slug: string;
  team_name: string;
  org_id: string;
  org_name: string;
  org_plan: string;
  role: string;
  is_active: boolean;
}

function readCache<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* sessionStorage may be disabled (Safari private, quota, …) */
  }
}

function clearCache(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Drop every cached loader payload. Call this BEFORE
 * ``revalidator.revalidate()`` (or any nav that should re-fetch from
 * the network) whenever the user has just done something that
 * invalidates ``/me`` or ``/me/teams``: sign-out, team switch, team
 * create, profile edit.
 */
export function invalidateAuthCaches(): void {
  clearCache(ME_CACHE_KEY);
  clearCache(TEAMS_CACHE_KEY);
}

/**
 * Optimistically move the cached dashboard identity into ``activeTeam``.
 *
 * The backend has already accepted the switch/create call and set a new
 * HttpOnly ``kos_session`` cookie. Waiting for ``/me`` + ``/me/teams``
 * before updating chrome makes the workspace switch feel broken, so we
 * prime the exact two loader caches the dashboard reads and then let
 * the loader's normal background refresh reconcile against the server.
 */
export function primeActiveTeamCaches(
  auth: AuthState,
  teams: CachedTeam[],
  activeTeam: CachedTeam,
): void {
  const nextAuth: AuthState = {
    ...auth,
    tenant: {
      org_id: activeTeam.org_id,
      team_id: activeTeam.team_id,
      team_slug: activeTeam.team_slug,
      team_name: activeTeam.team_name,
      org_name: activeTeam.org_name,
      org_plan: activeTeam.org_plan,
      role: activeTeam.role,
    },
  };
  writeCache(ME_CACHE_KEY, nextAuth);

  const seen = new Set<string>();
  const nextTeams = [activeTeam, ...teams]
    .filter((team) => {
      if (seen.has(team.team_id)) return false;
      seen.add(team.team_id);
      return true;
    })
    .map((team) => ({
      ...team,
      is_active: team.team_id === activeTeam.team_id,
    }));
  writeCache(TEAMS_CACHE_KEY, nextTeams);
}

function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

/**
 * Resolve the current viewer (`/v1/me`) for the route subtree, or
 * redirect to /login if no session cookie is attached.
 *
 * On a refresh we return the sessionStorage cache immediately and let
 * the network call run in the background; the cache is overwritten
 * when fresh data arrives so the next navigation reflects any updates
 * (e.g. role change). On 401 we drop the cache and redirect.
 */
export async function requireAuth(request: Request): Promise<AuthState> {
  const cached = readCache<AuthState>(ME_CACHE_KEY);

  // Always start a network fetch so the cache stays warm. We tear it
  // down on 401 so a stale cookie doesn't keep the user "logged in"
  // forever.
  const refresh = (async (): Promise<AuthState | null> => {
    try {
      const me = await api<MeResponse>("/me");
      const fresh = meToAuthState(me);
      writeCache(ME_CACHE_KEY, fresh);
      return fresh;
    } catch (err) {
      if (isAuthError(err)) {
        invalidateAuthCaches();
        return null;
      }
      throw err;
    }
  })();

  if (cached) {
    // Don't await — let the refresh run in the background. The next
    // navigation picks up the fresh value via the cache.
    void refresh.catch(() => {
      /* swallow: background errors don't tear down a working session */
    });
    return cached;
  }

  // No cache: the loader has to wait for fresh data to know whether
  // to redirect to /login. ``refresh`` already converts auth errors
  // to ``null`` and rethrows everything else.
  const fresh = await refresh;
  if (!fresh) {
    const url = new URL(request.url);
    const from = url.pathname + url.search;
    const params = new URLSearchParams();
    if (from && from !== "/login") params.set("from", from);
    const qs = params.toString();
    throw redirect(qs ? `/login?${qs}` : "/login");
  }
  return fresh;
}

/**
 * Fetch ``/v1/me/teams`` for the workspace switcher, with the same
 * stale-while-revalidate pattern as ``requireAuth``. Network errors
 * never surface — the switcher just hides itself when teams is empty.
 */
export async function loadTeams(): Promise<CachedTeam[]> {
  const cached = readCache<CachedTeam[]>(TEAMS_CACHE_KEY);

  const refresh = (async (): Promise<CachedTeam[]> => {
    try {
      const body = await api<{ items: CachedTeam[] }>("/me/teams");
      const items = body.items ?? [];
      writeCache(TEAMS_CACHE_KEY, items);
      return items;
    } catch (err) {
      if (isAuthError(err)) {
        invalidateAuthCaches();
        return [];
      }
      console.warn(`[shell] /v1/me/teams failed: ${err instanceof Error ? err.message : err}`);
      return [];
    }
  })();

  if (cached) {
    void refresh; // background refresh; never throws
    return cached;
  }
  return refresh;
}

/**
 * Inverse of `requireAuth`: when the viewer is already signed in,
 * bounce them away from the public surface (marketing home / login)
 * and into the dashboard (preserving `?from=...` if present).
 *
 * Returns null when the viewer is signed out so the caller can
 * render the public route as usual.
 */
export async function redirectIfAuthed(request: Request): Promise<null> {
  // Fast path: trust the sessionStorage cache. If it's there, the
  // cookie was valid the last time the SPA rendered, so we kick the
  // user into /chat without paying for /me. Cookie expiry is handled
  // on the dashboard side via ``requireAuth``.
  if (readCache<AuthState>(ME_CACHE_KEY)) {
    const url = new URL(request.url);
    const from = url.searchParams.get("from");
    const target = from?.startsWith("/") ? from : "/chat";
    throw redirect(target);
  }

  try {
    await api<MeResponse>("/me");
  } catch (err) {
    if (isAuthError(err)) {
      return null;
    }
    // Non-auth errors (gateway down, network) shouldn't kick the user
    // off the marketing page. Surface them via the route — the user
    // can still hit "Sign in" and try the OAuth flow.
    return null;
  }
  const url = new URL(request.url);
  const from = url.searchParams.get("from");
  const target = from?.startsWith("/") ? from : "/chat";
  throw redirect(target);
}
